import React, { Suspense, useState, useRef, useEffect } from 'react';
import { Canvas, useLoader, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, Bounds, PerspectiveCamera, meshBounds } from '@react-three/drei';
import { STLLoader } from 'three-stdlib';
import * as THREE from 'three';

// STL Model Component using the geopd-dev approach
interface STLModelProps {
  url: string;
}

const STLModel = ({ url }: STLModelProps) => {
  const geometry = useLoader(STLLoader, url);

  return (
    <mesh raycast={meshBounds}>
      <primitive object={geometry} attach="geometry" />
      <meshStandardMaterial color="orange" />
    </mesh>
  );
};

// Marker Component with optimized continuous position tracking
interface MarkerProps {
  position: [number, number, number];
  onClick?: (event: any) => void;
  isActive?: boolean;
  label?: string;
  id: string;
  onPositionUpdate?: (screenPosition: { x: number; y: number }) => void;
  color?: string; // Add color prop for tolerance compliance
}

const Marker = ({ position, onClick, isActive = false, label, id, onPositionUpdate, color }: MarkerProps) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const { camera, gl } = useThree();
  const lastPosition = useRef<{ x: number; y: number } | null>(null);

  // Use useFrame for continuous tracking with optimization
  useFrame(() => {
    if (meshRef.current && onPositionUpdate) {
      // Get world position of marker (like geopd-dev: mesh.object.localToWorld)
      const worldPosition = new THREE.Vector3();
      meshRef.current.localToWorld(worldPosition);
      
      // Project to normalized device coordinates (like geopd-dev: pos.project)
      const screenPosition = worldPosition.clone().project(camera);
      
      // Convert to pixel coordinates (like geopd-dev calculation)
      const canvas = gl.domElement;
      const rect = canvas.getBoundingClientRect();
      const widthHalf = rect.width / 2;
      const heightHalf = rect.height / 2;
      
      const x = (screenPosition.x * widthHalf) + widthHalf + rect.left;
      const y = -(screenPosition.y * heightHalf) + heightHalf + rect.top;
      
      // Only update if position changed significantly (reduce unnecessary updates)
      if (!lastPosition.current || 
          Math.abs(x - lastPosition.current.x) > 1 || 
          Math.abs(y - lastPosition.current.y) > 1) {
        lastPosition.current = { x, y };
        onPositionUpdate({ x, y });
      }
    }
  });

  const handleClick = (event: any) => {
    if (onClick) {
      onClick(event);
    }
  };

  // Determine marker color based on tolerance compliance (don't change for active state)
  const markerColor = color || 'royalblue';

  return (
    <group position={position}>
      {/* Glow effect for selected markers */}
      {isActive && (
        <mesh scale={4.5}>
          <sphereGeometry />
          <meshBasicMaterial 
            color={markerColor} 
            transparent 
            opacity={0.3}
            side={THREE.BackSide}
          />
        </mesh>
      )}
      
      {/* Main marker */}
      <mesh 
        ref={meshRef}
        name={id}
        scale={3}
        onClick={handleClick}
      >
        <sphereGeometry />
        <meshStandardMaterial color={markerColor} />
      </mesh>
    </group>
  );
};

// Loading Fallback
const LoadingFallback = () => {
  return (
    <mesh>
      <boxGeometry args={[10, 10, 10]} />
      <meshStandardMaterial color="#cccccc" transparent opacity={0.5} />
    </mesh>
  );
};

// Main Interactive 3D Viewer Component
interface Interactive3DViewerProps {
  stlUrl: string;
  markerPositions?: Array<{
    id: string;
    featureLabel: string;
    position: [number, number, number];
    color?: string; // Add color prop for tolerance compliance
  }>;
  onMarkerClick?: (markerId: string, screenPosition?: { x: number; y: number }, event?: any) => void;
  onMarkerPositionUpdate?: (markerId: string, screenPosition: { x: number; y: number }) => void;
  activeMarkerId?: string;
  className?: string;
}

const Interactive3DViewer = ({
  stlUrl,
  markerPositions = [],
  onMarkerClick,
  onMarkerPositionUpdate,
  activeMarkerId,
  className = ''
}: Interactive3DViewerProps) => {
  const [error, setError] = useState<string | null>(null);
  const [markerScreenPositions, setMarkerScreenPositions] = useState<Record<string, { x: number; y: number }>>({});
  const cameraRef = useRef();

  const handleMarkerClick = (markerId: string, event?: any) => {
    if (onMarkerClick) {
      const screenPosition = markerScreenPositions[markerId];
      onMarkerClick(markerId, screenPosition, event);
    }
  };

  const handleMarkerPositionUpdate = (markerId: string, screenPosition: { x: number; y: number }) => {
    setMarkerScreenPositions(prev => ({
      ...prev,
      [markerId]: screenPosition
    }));
    
    // Also call the parent callback for continuous updates
    if (onMarkerPositionUpdate) {
      onMarkerPositionUpdate(markerId, screenPosition);
    }
  };

  if (error) {
    return (
      <div className={`w-full h-full flex items-center justify-center bg-red-50 ${className}`}>
        <div className="text-center">
          <div className="text-red-600 mb-2">Failed to load 3D model</div>
          <div className="text-sm text-red-500">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full ${className}`} style={{ minHeight: '400px' }}>
      <Canvas
        shadows
        style={{ background: '#f8f9fa' }}
        onCreated={({ gl, scene, camera }) => {
          // Canvas created successfully
        }}
        onError={(error: any) => {
          setError(error?.message || 'Canvas error occurred');
        }}
      >
        {/* Camera - matching geopd-dev exactly */}
        <PerspectiveCamera makeDefault ref={cameraRef} />
        
        {/* Lighting - matching geopd-dev exactly */}
        <directionalLight intensity={4.5} />
        <ambientLight intensity={1.5} />

        {/* STL Model with Bounds - no transformations */}
        <Bounds fit>
          <STLModel url={stlUrl} />
        </Bounds>

        {/* Markers - separate group, no transformations */}
        <group>
          {markerPositions.map((marker) => (
            <Marker
              key={marker.id}
              id={marker.id}
              position={marker.position}
              onClick={(event) => handleMarkerClick(marker.id, event)}
              isActive={activeMarkerId === marker.id}
              label={marker.featureLabel}
              onPositionUpdate={(screenPos) => handleMarkerPositionUpdate(marker.id, screenPos)}
              color={marker.color}
            />
          ))}
        </group>

        {/* Controls - no damping/inertia for precise tracking */}
        <OrbitControls 
          makeDefault 
          enableDamping={false}  // Disable inertia/momentum completely
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
        />
      </Canvas>

      {/* Instructions Overlay */}
      <div className="absolute bottom-4 left-4 bg-black bg-opacity-75 text-white p-3 rounded-lg text-sm max-w-xs">
        <div className="space-y-1">
          <div>🖱️ <strong>Left Click + Drag:</strong> Rotate</div>
          <div>🔄 <strong>Right Click + Drag:</strong> Pan</div>
          <div>🔍 <strong>Mouse Wheel:</strong> Zoom</div>
          <div>📍 <strong>Blue Spheres:</strong> Click for data</div>
        </div>
      </div>
    </div>
  );
};

export default Interactive3DViewer; 